<template>
  <div
    class="pdm"
    :class="state.overflow ? `pdm--out-${state.overflow}` : ''"
    dir="ltr"
    tabindex="0"
    role="img"
    :aria-label="summary"
    v-tooltip="{ content: tooltip, html: false }"
  >
    <div class="pdm-top">
      <span
        v-if="state.kind === 'live'"
        class="pdm-pill"
        :class="`pdm-pill--${state.tone}`"
        :style="pillStyle"
      >
        <bdi dir="ltr">{{ percent }}</bdi>
        <span dir="rtl">{{ __('to entry') }}</span>
        <span v-if="state.overflow" dir="rtl">· {{ __('Out of range') }}</span>
        <span v-if="stale" dir="rtl">· {{ __('Stale price') }}</span>
      </span>
      <span
        v-else
        class="pdm-pill"
        :class="state.kind === 'settled' ? (state.outcome === 'profit' ? 'pdm-pill--profit' : 'pdm-pill--loss') : 'pdm-pill--muted'"
        :style="state.kind === 'settled' ? { left: '50%', transform: 'translateX(-50%)' } : null"
        dir="rtl"
      >{{ plainText }}</span>
    </div>

    <div class="pdm-track">
      <div
        class="pdm-bar"
        :class="{
          'pdm-bar--live': state.kind === 'live',
          'pdm-bar--open': meter.state === 'OPEN',
          [`pdm-bar--hit-${state.outcome}`]: state.kind === 'settled',
        }"
      >
        <span class="pdm-half pdm-half--sl" />
        <span class="pdm-half pdm-half--tp" />
        <span class="pdm-entry" />

        <template v-if="state.kind === 'live'">
          <span v-if="state.overflow" class="pdm-link" :class="`pdm-link--${state.overflow}`" />
          <span
            class="pdm-marker"
            :class="[`pdm-marker--${state.tone}`, `pdm-marker--${state.markerStyle}`]"
            :style="markerStyle"
          />
          <span v-if="state.overflow" class="pdm-arrow" :class="[`pdm-arrow--${state.overflow}`, `pdm-arrow--${state.tone}`]" />
        </template>
        <span
          v-else-if="state.kind === 'settled'"
          class="pdm-marker pdm-marker--filled"
          :class="state.outcome === 'profit' ? 'pdm-marker--profit' : 'pdm-marker--loss'"
          :style="{ left: `${state.edgePosition}%` }"
        />
        <span v-else class="pdm-marker pdm-marker--inactive" style="left: 50%" />
      </div>
    </div>

    <div class="pdm-labels" :class="[{ 'pdm-labels--live': state.kind === 'live' }, state.kind === 'settled' ? `pdm-labels--hit-${state.outcome}` : '']">
      <span class="pdm-col pdm-col--sl">
        <span class="pdm-label-sl">SL</span>
        <span class="pdm-price">{{ meter.stopLossText }}</span>
      </span>
      <span class="pdm-col pdm-col--entry">
        <span class="pdm-label-entry" dir="rtl">{{ __('Entry') }}</span>
        <span class="pdm-price pdm-price--entry" :class="`pdm-price--${meter.side === 'SHORT' ? 'short' : 'long'}`">{{ meter.entryText }}</span>
      </span>
      <span class="pdm-col pdm-col--tp">
        <span class="pdm-label-tp">TP</span>
        <span class="pdm-price">{{ meter.takeProfitText }}</span>
      </span>
    </div>
  </div>
</template>

<script>
import { reactive } from 'vue'
import { calculateMeter, formatMeterPercent } from '../../utils/priceDistanceMeter'

// One shared clock so staleness re-evaluates without a timer per table row.
const clock = reactive({ now: Date.now() })
if (typeof window !== 'undefined' && !window.__pdmClock) {
  window.__pdmClock = setInterval(() => {
    clock.now = Date.now()
  }, 15000)
}

export default {
  props: ['resourceName', 'field'],

  computed: {
    meter() {
      return this.field.meter || {}
    },

    price() {
      const live = this.field.livePrice ?? this.meter.livePrice

      return live === undefined || live === null ? null : Number(live)
    },

    priceAt() {
      return this.field.livePriceAt ?? this.meter.livePriceAt ?? null
    },

    stale() {
      if (!this.priceAt || this.state.kind !== 'live') return false
      const age = (clock.now - new Date(this.priceAt).getTime()) / 1000

      return age > (this.meter.staleAfterSeconds || 120)
    },

    state() {
      const result = calculateMeter({
        side: this.meter.side,
        state: this.meter.state,
        entry: Number(this.meter.entry),
        stopLoss: Number(this.meter.stopLoss),
        takeProfit: Number(this.meter.takeProfit),
        price: this.price,
        tolerance: Number(this.meter.priceTolerance),
        outcome: this.meter.outcome,
      })

      return { kind: result.mode, ...result }
    },

    percent() {
      return this.state.kind === 'live' ? formatMeterPercent(this.state, this.meter.state, true) : ''
    },

    plainText() {
      switch (this.state.kind) {
        case 'settled':
          return this.state.outcome === 'profit' ? this.__('Take-profit hit') : this.__('Stop-loss hit')
        case 'inactive':
          return this.meter.state === 'CLOSED' ? this.__('Closed') : this.__('Cancelled')
        case 'missing-price':
          return this.__('Price unavailable')
        default:
          return this.__('Invalid range')
      }
    },

    markerStyle() {
      if (this.state.overflow) {
        return { left: this.state.overflow === 'SL' ? '-50px' : 'calc(100% + 50px)' }
      }

      return { left: `${this.state.edgePosition}%` }
    },

    pillStyle() {
      if (this.state.overflow) return { left: '50%', transform: 'translateX(-50%)' }
      // Edge-proportional anchor: the pill slides along the bar but never leaves it
      // (pos 0 aligns its left edge to the bar start, 100 aligns its right edge to the end).
      const pos = this.state.edgePosition

      return { left: `${pos}%`, transform: `translateX(-${pos}%)` }
    },

    summary() {
      const where = {
        profit: this.__('Profit side'),
        loss: this.__('Loss side'),
        neutral: this.__('On entry'),
        pending: this.__('Waiting for entry'),
      }

      if (this.state.kind !== 'live') return this.plainText

      return [
        this.meter.state === 'OPEN' ? this.__('Open trade') : this.__('Waiting for entry'),
        where[this.state.tone],
        `${this.percent} ${this.__('to entry')}`,
        this.state.overflow ? this.__('Out of range') : this.__('Inside the range'),
      ].join('، ')
    },

    tooltip() {
      const fmt = value => (Number.isFinite(Number(value)) ? Number(value).toFixed(this.meter.decimals ?? 2) : '—')
      const lines = [
        `${this.__('Current price')}: ${this.price === null ? '—' : fmt(this.price)}`,
        `${this.__('Entry')}: ${fmt(this.meter.entry)}`,
        `SL: ${fmt(this.meter.stopLoss)}`,
        `TP: ${fmt(this.meter.takeProfit)}`,
        `${this.summary}`,
      ]
      if (this.state.kind === 'live') {
        lines.push(this.__('Price change vs entry; no leverage or fees.'))
      }
      if (this.stale && this.priceAt) {
        lines.push(`${this.__('Stale price')}: ${this.priceAt}`)
      }

      return lines.join('\n')
    },
  },
}
</script>

<style>
.pdm {
  --profit: #16a34a;
  --profit-soft: #e4f6ea;
  --loss: #dc2626;
  --loss-soft: #fde8e8;
  --pending: #2563eb;
  --pending-soft: #e6effd;
  --neutral: #94a3b8;
  position: relative;
  box-sizing: border-box;
  min-width: 290px;
  max-width: 340px;
  outline: none;
}

.pdm:focus-visible {
  border-radius: 8px;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.45);
}

/* Out of range reads purple: the pill, the dashes, the arrow and a waiting diamond. */
.pdm--out-SL .pdm-pill,
.pdm--out-TP .pdm-pill { background: #ede9fe; color: #7c3aed; }
.pdm--out-SL .pdm-link,
.pdm--out-TP .pdm-link { border-top-color: #c4b5fd; }
.pdm--out-SL .pdm-arrow,
.pdm--out-TP .pdm-arrow { color: #7c3aed; }
.pdm--out-SL .pdm-marker--outline,
.pdm--out-TP .pdm-marker--outline { border-color: #7c3aed; }

/* Out of range: the bar gives up room on that side for the diamond, dashes and arrow. */
.pdm--out-SL .pdm-track,
.pdm--out-SL .pdm-labels { padding-left: 76px; }
.pdm--out-TP .pdm-track,
.pdm--out-TP .pdm-labels { padding-right: 76px; }

.pdm-top {
  position: relative;
  height: 34px;
}

.pdm-pill {
  position: absolute;
  top: 0;
  display: inline-flex;
  gap: 5px;
  align-items: center;
  padding: 3px 12px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  transition: left 0.2s ease, transform 0.2s ease;
}

.pdm-pill--muted {
  left: 50%;
  transform: translateX(-50%);
  background: #f1f2f6;
  color: #6b7280;
}

.pdm-pill--profit { background: var(--profit-soft); color: var(--profit); font-weight: 700; }
.pdm-pill--loss { background: var(--loss-soft); color: var(--loss); font-weight: 700; }
.pdm-pill--pending { background: var(--pending-soft); color: var(--pending); }
.pdm-pill--neutral { background: #eef0f5; color: #64748b; font-weight: 700; }

.pdm-track {
  position: relative;
  height: 14px;
}

.pdm-bar {
  position: absolute;
  top: 2px;
  right: 0;
  left: 0;
  display: flex;
  height: 9px;
  border-radius: 9999px;
  background: linear-gradient(90deg, #dcdde3, #e6e7ec 50%, #dcdde3);
}
.pdm--out-SL .pdm-bar { left: 76px; }
.pdm--out-TP .pdm-bar { right: 76px; }

.pdm-bar--hit-profit { background: linear-gradient(90deg, #e6e8f0 50%, #22c55e); }
.pdm-bar--hit-loss { background: linear-gradient(90deg, #ef4444, #e6e8f0 50%); }
.pdm-labels--hit-profit .pdm-label-tp { color: #16a34a; font-weight: 700; }
.pdm-labels--hit-loss .pdm-label-sl { color: #dc2626; font-weight: 700; }
.pdm-labels--hit-profit .pdm-price--entry,
.pdm-labels--hit-loss .pdm-price--entry { color: #374151; }

.pdm-half { flex: 1; opacity: 0; }
.pdm-bar--live .pdm-half { opacity: 1; }
.pdm-half--sl { border-radius: 9999px 0 0 9999px; background: linear-gradient(90deg, #6b7280, #e6e8f0); }
.pdm-half--tp { border-radius: 0 9999px 9999px 0; background: linear-gradient(90deg, #e6e8f0, #3b82f6); }

.pdm-entry {
  position: absolute;
  top: -1px;
  left: 50%;
  width: 2px;
  height: 11px;
  background: #3b4a6b;
  transform: translateX(-50%);
}

.pdm-marker {
  position: absolute;
  top: 50%;
  width: 13px;
  height: 13px;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.22);
  transform: translate(-50%, -50%) rotate(45deg);
  transition: left 0.2s ease;
}

.pdm-marker--filled.pdm-marker--profit { background: #22c55e; }
.pdm-marker--filled.pdm-marker--loss { background: #ef4444; }
.pdm-marker--filled.pdm-marker--neutral { background: var(--neutral); }
.pdm-marker--outline { width: 14px; height: 14px; border: 2.5px solid #2f6df6; background: #fff; box-shadow: none; }
.pdm-marker--inactive { background: #a3a8b8; }

.pdm-link {
  position: absolute;
  top: 50%;
  width: 36px;
  border-top: 2px dashed #b4b8c5;
  transform: translateY(-50%);
}
.pdm-link--SL { right: 100%; }
.pdm-link--TP { left: 100%; }

.pdm-arrow {
  position: absolute;
  top: 50%;
  width: 9px;
  height: 9px;
  border-top: 2.5px solid currentColor;
  border-left: 2.5px solid currentColor;
}
.pdm-arrow--SL { right: calc(100% + 68px); transform: translateY(-50%) rotate(-45deg); }
.pdm-arrow--TP { left: calc(100% + 68px); transform: translateY(-50%) rotate(135deg); }
.pdm-arrow--pending { color: #2f6df6; }
.pdm-arrow--profit { color: var(--profit); }
.pdm-arrow--loss { color: var(--loss); }
.pdm-arrow--neutral { color: #64748b; }

.pdm-labels {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  margin-top: 10px;
  font-size: 13px;
  line-height: 18px;
  color: #9aa0b4;
}
.pdm-col { display: flex; flex-direction: column; }
.pdm-col--sl { align-items: flex-start; }
.pdm-col--entry { align-items: center; }
.pdm-col--tp { align-items: flex-end; }

.pdm-price {
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #6b7280;
}
.pdm-labels--live .pdm-col--sl .pdm-price { color: #4b5563; }
.pdm-labels--live .pdm-col--tp .pdm-price { color: #2f6df6; }
.pdm-price--entry { font-size: 13px; font-weight: 700; }
.pdm-labels--live .pdm-price--long { color: #16a34a; }
.pdm-labels--live .pdm-price--short { color: #dc2626; }

.pdm-labels--live .pdm-label-sl { color: #6b7280; }
.pdm-labels--live .pdm-label-tp { color: #2f6df6; }
.pdm-labels--live .pdm-label-entry { color: #4a5578; }

html.dark .pdm { --profit-soft: rgba(34, 197, 94, 0.18); --loss-soft: rgba(239, 68, 68, 0.18); --pending-soft: rgba(59, 130, 246, 0.2); }
html.dark .pdm-pill--muted { background: rgba(255, 255, 255, 0.08); color: #a1a1aa; }
html.dark .pdm-pill--profit { color: #86efac; }
html.dark .pdm-pill--loss { color: #fca5a5; }
html.dark .pdm-pill--pending { color: #93c5fd; }
html.dark .pdm-pill--neutral { background: rgba(255, 255, 255, 0.08); color: #cbd5e1; }
html.dark .pdm-price { color: #a1a1aa; }
html.dark .pdm-labels--live .pdm-col--sl .pdm-price { color: #d4d4d8; }
html.dark .pdm-labels--live .pdm-col--tp .pdm-price { color: #93c5fd; }
html.dark .pdm--out-SL .pdm-pill,
html.dark .pdm--out-TP .pdm-pill { background: rgba(139, 92, 246, 0.22); color: #c4b5fd; }
html.dark .pdm-labels--live .pdm-price--long { color: #86efac; }
html.dark .pdm-labels--live .pdm-price--short { color: #fca5a5; }
html.dark .pdm-labels--live .pdm-label-entry { color: #d4d4d8; }
html.dark .pdm-entry { background: #cbd5e1; }
html.dark .pdm-bar { background: linear-gradient(90deg, #52525b, #6b6b74 50%, #52525b); }
html.dark .pdm-half--sl { background: linear-gradient(90deg, #a1a1aa, #3f3f46); }
html.dark .pdm-half--tp { background: linear-gradient(90deg, #3f3f46, #3b82f6); }
html.dark .pdm-bar--hit-profit { background: linear-gradient(90deg, #3f3f46 50%, #22c55e); }
html.dark .pdm-bar--hit-loss { background: linear-gradient(90deg, #ef4444, #3f3f46 50%); }
html.dark .pdm-labels { color: #71717a; }
html.dark .pdm-labels--live .pdm-label-sl { color: #a1a1aa; }
html.dark .pdm-labels--live .pdm-label-tp { color: #60a5fa; }
html.dark .pdm-labels--hit-profit .pdm-label-tp { color: #4ade80; }
html.dark .pdm-labels--hit-loss .pdm-label-sl { color: #f87171; }
html.dark .pdm-labels--hit-profit .pdm-price--entry,
html.dark .pdm-labels--hit-loss .pdm-price--entry { color: #d4d4d8; }
html.dark .pdm-marker { border-color: #18181b; box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.18); }
html.dark .pdm-marker--inactive { background: #8b8b95; }
html.dark .pdm--out-SL .pdm-arrow,
html.dark .pdm--out-TP .pdm-arrow,
html.dark .pdm--out-SL .pdm-marker.pdm-marker--outline,
html.dark .pdm--out-TP .pdm-marker.pdm-marker--outline { border-color: #a78bfa; }
html.dark .pdm--out-SL .pdm-arrow,
html.dark .pdm--out-TP .pdm-arrow { color: #a78bfa; }
html.dark .pdm--out-SL .pdm-link,
html.dark .pdm--out-TP .pdm-link { border-top-color: #6d5bb0; }
html.dark .pdm-marker.pdm-marker--outline { border-color: #60a5fa; background: #18181b; box-shadow: none; }

@media (prefers-reduced-motion: reduce) {
  .pdm-pill,
  .pdm-marker { transition: none; }
}
</style>
